import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {isEmpty} from "../commonmethod/CommonMethod";
import Modal from "../modal/Modal";
import {ModalTable, ModalTd, ModalTh} from "../progressBar/LicenseProgressBar";
import {ChevronDownIcon} from "../selector/SelectorIcons";
import {
    applySearchDrawerDropdownSizing,
    computeDropdownFixedPosition,
    getDropdownPortalStyle,
} from "../../utils/selectbox/dropdownViewportPosition";
import "../selector/selector.css";
import "../searchSelector/searchSelector.css";
import "./simmsSelect.css";

const INLINE_DROPDOWN_MIN_WIDTH = 120;
const INLINE_DROPDOWN_ESTIMATED_HEIGHT = 200;

function SimmsSelect({options, onChange, style, required, data, readOnly, variant, ...rest}) {
    const isTableSize = variant === "tableSize";
    const [currentLabel, setCurrentLabel] = useState(() => {
        if (!options?.length) return "";
        const matchedOption = data != null ? options.find((e) => e.id === parseInt(data, 10)) : null;
        return matchedOption ? matchedOption.name : options[0].name;
    });
    const [isShowOptions, setShowOptions] = useState(false);
    const clickRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({top: 0, left: 0});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modal = rest?.modal;

    useEffect(() => {
        const matchedOption = data != null ? options.find((e) => e.id === parseInt(data, 10)) : null;
        setCurrentLabel(matchedOption ? matchedOption.name : options?.[0]?.name ?? "");
    }, [data, options]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (isModalOpen) return;
            const inWrapper = clickRef.current?.contains(event.target);
            const inDropdown = dropdownRef.current?.contains(event.target);
            if (!inWrapper && !inDropdown) {
                setShowOptions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isModalOpen]);

    useLayoutEffect(() => {
        if (!isShowOptions || isTableSize) return;

        const updatePosition = () => {
            const triggerEl = triggerRef.current;
            if (!triggerEl) return;
            const pos = computeDropdownFixedPosition({
                triggerEl,
                dropdownEl: dropdownRef.current,
                dropdownMinWidth: INLINE_DROPDOWN_MIN_WIDTH,
                estimatedHeight: INLINE_DROPDOWN_ESTIMATED_HEIGHT,
                contentSized: true,
            });
            applySearchDrawerDropdownSizing(dropdownRef.current, triggerEl, pos, {widePanel: false});
            setDropdownPos(pos);
        };

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isShowOptions, isTableSize, options]);

    const handleOpenModal = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleUpdateInput = async (e) => {
        if (e.key === "Enter") {
            const val = e.currentTarget.value.trim();
            if (isEmpty(val)) {
                alert("옵션명을 입력해주세요");
                return;
            }
            const r = await modal.create(val, modal);
            if (r?.status === 200) {
                await modal.refresh();
            }
        }
    };

    const toggleOptions = () => {
        if (!readOnly) {
            setShowOptions((prev) => !prev);
        }
    };

    const handleSelect = (option, event) => {
        if (option.id === "추가") {
            handleOpenModal(event);
            return;
        }
        onChange(option.id);
        setCurrentLabel(option.name);
        setShowOptions(false);
    };

    const selectedId = data != null ? parseInt(data, 10) : null;
    const triggerClassName = isTableSize
        ? ["table-size-selector-trigger", isShowOptions ? "is-open" : ""].filter(Boolean).join(" ")
        : [
            "search-selector-trigger",
            "search-selector-trigger--compact",
            "simms-drawer-trigger",
            "simms-inline-select-trigger",
            currentLabel && currentLabel !== options?.[0]?.name ? "is-selected" : "",
            required && (data == null || data === "") ? "is-required-empty" : "",
            isShowOptions ? "is-open" : "",
        ].filter(Boolean).join(" ");

    const wrapperClassName = isTableSize ? "table-size-selector-field" : "simms-inline-select";
    const valueClassName = isTableSize ? "table-size-selector-value" : "search-selector-summary";
    const chevronClassName = isTableSize ? "table-size-selector-chevron" : "search-selector-chevron";
    const dropdownClassName = isTableSize
        ? "selector-dropdown table-size-selector-dropdown"
        : "selector-dropdown simms-inline-select-dropdown";

    const dropdownList = (
        <div className="selector-dropdown-list">
            {options.map((option, index) => {
                const isSelected = option.id !== "추가" && option.id === selectedId;
                return (
                    <div
                        key={`simms_select_${index}`}
                        className={`selector-option ${isSelected ? "is-selected" : ""}`}
                    >
                        <div
                            className="selector-option-select"
                            onClick={(event) => handleSelect(option, event)}
                        >
                            <span className="selector-option-name">{option.name}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <>
            <div className={wrapperClassName} style={style} ref={clickRef}>
                <div
                    ref={triggerRef}
                    className={triggerClassName}
                    role="button"
                    tabIndex={0}
                    onClick={toggleOptions}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleOptions();
                        }
                    }}
                >
                    <span className={valueClassName}>{currentLabel}</span>
                    <ChevronDownIcon className={`${chevronClassName} ${isShowOptions ? "is-open" : ""}`} />
                </div>

                {isShowOptions && isTableSize && (
                    <div className={dropdownClassName}>
                        {dropdownList}
                    </div>
                )}
            </div>

            {isShowOptions && !isTableSize && typeof document !== "undefined" && createPortal(
                <div
                    ref={dropdownRef}
                    className={`fixed z-[1200] selector-dropdown simms-inline-select-dropdown simms-inline-select-dropdown--portal`}
                    style={getDropdownPortalStyle(dropdownPos)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {dropdownList}
                </div>,
                document.body,
            )}

            {modal && (
                <Modal isOpen={isModalOpen} onClose={handleCloseModal} className="modal-top">
                    <h2 className="font-bold text-[20px]">{modal.title}</h2>
                    <ModalTable>
                        <tbody>
                            <tr>
                                <ModalTh padding="10px 0 10px 5px" align="left" background="#f1f6f9">
                                    옵션명
                                </ModalTh>
                                <ModalTd padding="10px 15px 10px 5px">
                                    <input
                                        autoFocus
                                        className="border indent-1 focus:outline-none focus:border-[#6f6ff5]"
                                        onKeyDown={handleUpdateInput}
                                    />
                                </ModalTd>
                            </tr>
                        </tbody>
                    </ModalTable>
                </Modal>
            )}
        </>
    );
}

export default SimmsSelect;
